import React from 'react';
import { useLocation } from 'react-router-dom';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Checkbox,
  Grid,
  Radio,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CustomContainer,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
  StyledDataGrid,
} from '../../../styles/styles';
import PageTitle from '../../molecules/PageTitle';
import NavigateButton from '../../atoms/Button/NavigateButton';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CircleOutlined, Close, Upload } from '@mui/icons-material';
import { fetchErrorText } from '../../../common/const';
import FirmwareUploadModal from './FirmwareUploadModal';
import NewOtaModal from './NewOtaModal';

interface Sdc {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  firmwareVersion: string;
  isOnline: boolean;
  isAalEnabled: boolean;
  isSciEnabled: boolean;
  building: {
    buildingManagementId: string;
  } | null;
}

interface FirmwareBinaryFile {
  createdAt: string;
  autoFirmwareBinaryFileId: number;
  fileName: string;
  memo: string;
}

const NewOtaPage: React.FC = () => {
  const location = useLocation();

  const [isSnackbarOpened, setIsSnackbarOpened] =
    React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('success');

  const [sdcs, setSdcs] = React.useState<Sdc[]>([]);
  const [isSdcsTableLoading, setIsSdcsTableLoading] =
    React.useState<boolean>(false);
  const [sdcUuidCheckedMap, setSdcUuidCheckedMap] = React.useState<
    Map<string, boolean>
  >(new Map());

  const [firmwares, setFirmwares] = React.useState<FirmwareBinaryFile[]>([]);
  const [isFirmwaresTableLoading, setIsFirmwaresTableLoading] =
    React.useState<boolean>(false);
  const [selectedFirmwareId, setSelectedFirmwareId] =
    React.useState<number>(-1);

  const [isUploadModalOpened, setIsUploadModalOpened] =
    React.useState<boolean>(false);

  const [isNewOtaModalOpened, setIsNewOtaModalOpened] =
    React.useState<boolean>(false);

  const openSnackBarWithSuccess = (text: string) => {
    setIsSnackbarOpened(true);
    setSnackbarText(text);
    setSnackbarSeverity('success');
  };

  const openSnackBarWithError = (text: string) => {
    setIsSnackbarOpened(true);
    setSnackbarText(text);
    setSnackbarSeverity('error');
  };

  const handleCloseSnackbar = () => {
    setIsSnackbarOpened(false);
  };

  const handlePressNewOtaButton = () => {
    if (selectedFirmwareId < 0) {
      openSnackBarWithError('対象ファームウェアを選択してください。');
      return;
    }
    if (Array.from(sdcUuidCheckedMap.values()).every((checked) => !checked)) {
      openSnackBarWithError('少なくとも1つのSDCを選択してください。');
      return;
    }
    setIsNewOtaModalOpened(true);
  };

  const fetchSdcs = async () => {
    setIsSdcsTableLoading(true);
    await getJwtTokenFromIdToken()
      .then((token) => {
        // call indexSdcs API
        return fetch(`${process.env.REACT_APP_API_ENDPOINT}/sdcs`, {
          headers: {
            Authorization: token,
          },
        });
      })
      .then((response) => {
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          openSnackBarWithError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (!body) {
          return;
        }
        setSdcs(body.sdcs);
      })
      .catch((e) => {
        openSnackBarWithError(fetchErrorText);
        console.error(e);
      })
      .finally(() => {
        setIsSdcsTableLoading(false);
      });
  };

  const fetchFirmwares = async () => {
    setIsFirmwaresTableLoading(true);
    await getJwtTokenFromIdToken()
      .then((token) => {
        // call listFirmwareBinaryFiles API
        return fetch(`${process.env.REACT_APP_API_ENDPOINT}/firmwares`, {
          headers: {
            Authorization: token,
          },
        });
      })
      .then((response) => {
        if (response.status !== 200) {
          console.error(`HTTP error status: ${response.status}`);
          openSnackBarWithError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((bodyObject) => {
        if (bodyObject !== null) {
          setFirmwares(bodyObject.firmwareBinaryFiles);
        }
      })
      .catch((e) => {
        openSnackBarWithError(fetchErrorText);
        console.error(e);
      })
      .finally(() => {
        setIsFirmwaresTableLoading(false);
      });
  };

  const sdcsTableColumns: GridColDef[] = [
    {
      field: 'check',
      headerName: '対象選択',
      flex: 1,
      renderCell: (params: GridRenderCellParams<Sdc>) => (
        <Checkbox
          checked={!!sdcUuidCheckedMap.get(params.row.sdcUuid)}
          onChange={(e) => {
            setSdcUuidCheckedMap((oldMap) => {
              const newMap = new Map(oldMap);
              newMap.set(params.row.sdcUuid, e.target.checked);
              return newMap;
            });
          }}
        />
      ),
    },
    {
      field: 'sdcSerialNumber',
      headerName: 'シリアルナンバー',
      flex: 2,
      renderCell: (params: GridRenderCellParams<Sdc, string>) => (
        <Tooltip title={`${params.value} (UUID: ${params.row.sdcUuid})`}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'isOnline',
      headerName: '接続状態',
      flex: 1,
      renderCell: (params: GridRenderCellParams<Sdc, boolean>) => (
        <Tooltip title={params.value ? 'オンライン' : 'オフライン'}>
          {params.value ? (
            <CircleOutlined sx={{ color: 'green' }} />
          ) : (
            <Close sx={{ color: 'red' }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: 'firmwareVersion',
      headerName: '起動済バージョン',
      flex: 2,
      renderCell: (params: GridRenderCellParams<Sdc, string>) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'buildingManagementId',
      headerName: '設置取付先コード（物件管理ID）',
      flex: 2,
      valueGetter: (params: GridRenderCellParams<Sdc, string>) =>
        params.row.building?.buildingManagementId || '',
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  const firmwaresTableColumns: GridColDef[] = [
    {
      field: 'isSelected',
      headerName: '対象選択',
      flex: 1,
      renderCell: (params: GridRenderCellParams<FirmwareBinaryFile>) => (
        <Radio
          checked={params.row.autoFirmwareBinaryFileId === selectedFirmwareId}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedFirmwareId(params.row.autoFirmwareBinaryFileId);
            }
          }}
        />
      ),
    },
    {
      field: 'fileName',
      headerName: 'ファイル名',
      flex: 2,
      renderCell: (
        params: GridRenderCellParams<FirmwareBinaryFile, string>
      ) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'createdAt',
      headerName: '登録日',
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<FirmwareBinaryFile, string>
      ) => {
        if (!params.value) {
          return <span />;
        }
        const dateObj = new Date(params.value);
        return (
          <Tooltip
            title={dateObj.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
          >
            <span>
              {dateObj.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'memo',
      headerName: 'メモ',
      flex: 2,
      renderCell: (
        params: GridRenderCellParams<FirmwareBinaryFile, string>
      ) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  React.useEffect(() => {
    // call GET APIs
    fetchSdcs();
    fetchFirmwares();

    // update list of OTA target SDCs when given as location.state
    if (location.state && location.state.targetSdcUuids) {
      setSdcUuidCheckedMap((oldMap) => {
        const newMap = new Map(oldMap);
        (location.state.targetSdcUuids as string[]).forEach((sdcUuid) => {
          newMap.set(sdcUuid, true);
        });
        return newMap;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CustomContainer maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <PageTitle title="新規OTA実行予約" />
            <NavigateButton
              to={`/ota-schedules`}
              buttonTitle={'OTA実行予約一覧'}
            />
          </Box>
        </Grid>
        <Grid item xs={7}>
          <Box alignItems="center" height="30px" marginBottom={'5px'}>
            <Typography variant="subtitle1">対象SDC選択</Typography>
          </Box>
          <div style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
            <StyledDataGrid
              rows={sdcs}
              columns={sdcsTableColumns}
              getRowId={(row) => row.autoSdcId}
              disableRowSelectionOnClick
              localeText={
                isSdcsTableLoading
                  ? customDataGridLocaleTextLoading
                  : sdcs.length === 0
                  ? customDataGridLocaleTextNoData
                  : {}
              }
            />
          </div>
        </Grid>

        <Grid item xs={5}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            height="30px"
            marginBottom={'5px'}
          >
            <Typography variant="subtitle1">対象ファームウェア選択</Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<Upload />}
              onClick={() => {
                setIsUploadModalOpened(true);
              }}
            >
              登録…
            </Button>
          </Box>
          <div
            style={{
              height: 'calc(100vh - 280px)',
              width: '100%',
              marginBottom: '10px',
            }}
          >
            <StyledDataGrid
              rows={firmwares}
              columns={firmwaresTableColumns}
              getRowId={(row) => row.autoFirmwareBinaryFileId}
              disableRowSelectionOnClick
              localeText={
                isFirmwaresTableLoading
                  ? customDataGridLocaleTextLoading
                  : firmwares.length === 0
                  ? customDataGridLocaleTextNoData
                  : {}
              }
            />
          </div>
          <Box display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="success"
              onClick={handlePressNewOtaButton}
            >
              OTA実行予約登録
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={isSnackbarOpened}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarText}
        </Alert>
      </Snackbar>

      <FirmwareUploadModal
        open={isUploadModalOpened}
        close={(registeredFirmware: FirmwareBinaryFile | null) => {
          if (registeredFirmware) {
            setFirmwares([...firmwares, registeredFirmware]);
          }
          setIsUploadModalOpened(false);
        }}
        openSnackBarWithSuccess={openSnackBarWithSuccess}
        openSnackBarWithError={openSnackBarWithError}
      />

      <NewOtaModal
        open={isNewOtaModalOpened}
        close={() => {
          setIsNewOtaModalOpened(false);
        }}
        firmware={{
          autoFirmwareBinaryFileId: selectedFirmwareId,
          fileName:
            firmwares.find((firmware) => {
              return firmware.autoFirmwareBinaryFileId === selectedFirmwareId;
            })?.fileName ?? '',
        }}
        sdcs={sdcs
          .filter((sdc) => {
            return sdcUuidCheckedMap.get(sdc.sdcUuid);
          })
          .map((sdc) => {
            return { sdcUuid: sdc.sdcUuid, serialNumber: sdc.sdcSerialNumber };
          })}
        openSnackBarWithError={openSnackBarWithError}
      />
    </CustomContainer>
  );
};

export default NewOtaPage;
